import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { FormControl } from '@thoughtspot/radiant-react/widgets/form-control';
import { IconColor, IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Modal } from '@thoughtspot/radiant-react/widgets/modal';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { shouldUsePrismForIAMFlows } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { postDataService, useCheckSslCertMutation, useDeleteLdapAdIamV1Mutation, useGetLdapAdIamV1Query, useSetActiveDirectory, useSetLdapAdIamV1Mutation, } from "../../../services/system/system-service";
import { BasePageContext } from "../../../contexts/basepage-context";
import { GenericError } from '../../generic-error/generic-error.container';
import { CreateModal, Header, WithQueryHandler } from '../../system/common';
import { ActionButtons, LearnMore, Mode, TranslationsKeys as TranslationsKeysCommon, } from '../../system/common/constants';
import { getConfig } from '../../system/common/form-builder-config';
import { getFormFields, parseFormData } from '../../system/common/utils';
import { TranslationsKeys, validatePort } from '../constants';
import { fetchError } from './active-directectory-util';
import styles from './active-directory.module.scss';
import { FormBuilder } from './form-builder';
const pageName = 'activeDirectory';
const initialData = {
    url: '',
    domain: '',
    search_base: '',
    is_configured: false,
    automatically_create_users: false,
    ssl: false,
};
export const ActiveDirectoryContainer = ({ showLoader, hideLoader, onSuccess, onError, }) => {
    const { t } = useTranslation();
    const userTestRef = useRef({ username: '', password: '' });
    const sslRef = useRef({ sslCertificate: null, certificateAlias: '' });
    const formDataRef = useRef({});
    const formBuilderRef = useRef(null);
    const [mode, setMode] = useState(Mode.VIEW);
    const [isSslRequired, setIsSslRequired] = useState(false);
    const initialActiveDirectoryConfig = useMemo(() => getConfig(pageName), []);
    const [activeDirectoryData, setActiveDirectoryData] = useState({
        ...initialData,
    });
    const [formFields, setFormFields] = useState([]);
    const [ldapAdPostData, setLdapAdPostData] = useState(null);
    const [isAdTestComponentVisible, setIsAdTestComponentVisible] = useState(false);
    const [isUserTestFormInvalid, setIsUserTestFormInvalid] = React.useState(false);
    const [isUserTestConfirmDisabled, setIsUserTestConfirmDisabled] = useState(true);
    const [deleteConfirm, setDeleteConfirm] = React.useState(false);
    const [sslFieldsError, setSslFieldsError] = React.useState({
        sslCertificate: '',
        certificateAlias: '',
    });
    const { metricsService } = BasePageContext();
    const { data: ldapAdResponse, loading: isLdapAdLoading, error: ldapAdError, } = useGetLdapAdIamV1Query();
    const getFieldConfig = (field) => {
        switch (field.key) {
            case 'automatically_create_users':
                return {
                    children: [
                        {
                            label: t(TranslationsKeysCommon.YES),
                            value: 'true',
                        },
                        {
                            label: t(TranslationsKeysCommon.NO),
                            value: 'false',
                        },
                    ],
                    label: field.config.label,
                    orientation: 'tray',
                };
            case 'ssl':
                return {
                    children: [
                        {
                            label: t(TranslationsKeysCommon.YES),
                            value: 'true',
                        },
                        {
                            label: t(TranslationsKeysCommon.NO),
                            value: 'false',
                        },
                    ],
                    label: field.config.label,
                    orientation: 'tray',
                };
            case 'is_configured':
                return {
                    ...field.config,
                    isTextValueColorLight: !activeDirectoryData?.is_configured,
                };
            default:
                return field.config;
        }
    };
    const updateFormFields = (formData = {}, errorMessages = {}) => {
        const fieldsToShow = [];
        if (!activeDirectoryData?.enable_ldap && mode === Mode.VIEW) {
            const configuredStatus = initialActiveDirectoryConfig.find((config) => config.key === 'is_configured');
            fieldsToShow.push({
                ...configuredStatus,
                configCallback: getFieldConfig,
            });
        }
        else {
            initialActiveDirectoryConfig.forEach((config) => {
                if (mode === Mode.VIEW && config.view) {
                    const newConfig = config;
                    if (config.key === 'automatically_create_users') {
                        config.config.label = t(TranslationsKeys.AD_USER_ADDED_IN_TS);
                    }
                    fieldsToShow.push({
                        ...config,
                        configCallback: getFieldConfig,
                    });
                }
                else if (mode === Mode.EDIT && config.elementType) {
                    if (config.key === 'automatically_create_users') {
                        config.config.label = t(TranslationsKeysCommon.AD_MAPPING_AUTO_ADD_USER);
                    }
                    fieldsToShow.push({
                        ...config,
                        configCallback: getFieldConfig,
                    });
                }
            });
        }
        const newFormFields = getFormFields(formData, fieldsToShow, errorMessages, mode);
        setFormFields(newFormFields);
    };
    const fetchActiveDirectory = () => {
        if (ldapAdResponse && !isLdapAdLoading && ldapAdResponse.Data) {
            const newData = ldapAdResponse.Data || {};
            if (!newData?.ssl) {
                newData.ssl = false;
            }
            setActiveDirectoryData(newData?.enable_ldap ? { ...newData } : initialData);
        }
        else if (ldapAdResponse && !isLdapAdLoading && ldapAdResponse.Error) {
            onError(t(TranslationsKeys.MESSAGE_API_FAILURE));
        }
        hideLoader();
    };
    useEffect(() => {
        showLoader();
    }, []);
    useEffect(() => {
        fetchActiveDirectory();
    }, [ldapAdResponse]);
    useEffect(() => {
        if (!_.isEmpty(activeDirectoryData)) {
            updateFormFields(activeDirectoryData);
        }
    }, [activeDirectoryData, mode]);
    const validateForm = (currentFormData = {}, editedField = '') => {
        let anyInValid = false;
        if (editedField) {
            const editedData = currentFormData.filter((i) => i.id === editedField)[0] ||
                {};
            if (editedData?.valid === false) {
                anyInValid = true;
            }
            else if (editedData.id === 'url' &&
                !validatePort(editedData.value)) {
                editedData.valid = false;
                editedData.validation = {
                    ...editedData.validation,
                    error: t(TranslationsKeys.AD_ERROR_INVALID_PORT),
                };
                anyInValid = true;
            }
        }
        else {
            currentFormData.forEach((i) => {
                if (i && i.id === 'url' && i.valid && !validatePort(i.value)) {
                    i.valid = false;
                    i.validation = {
                        ...i.validation,
                        error: t(TranslationsKeys.AD_ERROR_INVALID_PORT),
                    };
                    anyInValid = true;
                }
                else if (i.valid === false) {
                    anyInValid = true;
                }
            });
        }
        return anyInValid;
    };
    const onSave = (currentFormData) => {
        let isFormInValid = validateForm(currentFormData);
        if (isSslRequired) {
            if (!sslRef.current.sslCertificate &&
                !sslRef.current.certificateAlias) {
                setSslFieldsError({
                    sslCertificate: t(TranslationsKeys.AD_SSL_CERTIFICATE_ERROR),
                    certificateAlias: t(TranslationsKeys.AD_CERTIFICATE_ALIAS_ERROR),
                });
                isFormInValid = true;
            }
            else if (!sslRef.current.sslCertificate &&
                sslRef.current.certificateAlias) {
                setSslFieldsError({
                    certificateAlias: '',
                    sslCertificate: t(TranslationsKeys.AD_SSL_CERTIFICATE_ERROR),
                });
            }
            else if (sslRef.current.sslCertificate &&
                !sslRef.current.certificateAlias) {
                setSslFieldsError({
                    sslCertificate: '',
                    certificateAlias: t(TranslationsKeys.AD_CERTIFICATE_ALIAS_ERROR),
                });
            }
            else {
                setSslFieldsError({
                    sslCertificate: '',
                    certificateAlias: '',
                });
            }
        }
        if (!isFormInValid) {
            const postData = { ...parseFormData(currentFormData) };
            formDataRef.current = postData;
            setIsAdTestComponentVisible(true);
        }
    };
    const onSaveSuccess = (response) => {
        setActiveDirectoryData(response.Data || {});
        if (response.Data?.enable_ldap) {
            metricsService.collectEvent(new UserAction(userActionTypeEnum.CONFIGURE_LDAP_AD_IAM_V1), {
                autoAddADUsers: response.Data?.automatically_create_users,
                sslRequired: response.Data?.ssl,
            });
            setMode(Mode.VIEW);
            sslRef.current = { sslCertificate: null, certificateAlias: '' };
            onSuccess(t(TranslationsKeys.TOAST_CHANGES_SAVED));
        }
        else if (response.Error) {
            onError(response.Error);
        }
        hideLoader();
    };
    const onSaveError = (error) => {
        hideLoader();
        onError(t('admin.error.generic'));
    };
    const [updateLdapAd] = useSetLdapAdIamV1Mutation(response => onSaveSuccess(response), onSaveError, ldapAdPostData);
    const { postLdapAd, error, loading, response } = useSetActiveDirectory();
    const { data: sslCertData, error: sslCertError, loading: sslCertLoading, checkSSLCert, } = useCheckSslCertMutation();
    const [deleteLdapAd] = useDeleteLdapAdIamV1Mutation(response => {
        setMode(Mode.VIEW);
        hideLoader();
        const newData = response.Data || {};
        setActiveDirectoryData(newData?.enable_ldap ? newData : initialData);
        if (newData?.enable_ldap) {
            onSuccess(t(TranslationsKeys.DELETE_SUCCESS));
        }
        else {
            metricsService.collectEvent(new UserAction(userActionTypeEnum.REMOVE_LDAP_AD_IAM_V1), {});
        }
    }, onSaveError);
    useEffect(() => {
        if (error) {
            onSaveError(error);
        }
    }, [error]);
    useEffect(() => {
        if (ldapAdPostData) {
            showLoader();
            if (shouldUsePrismForIAMFlows()) {
                updateLdapAd(ldapAdPostData);
                return;
            }
            const config = {};
            config.data = ldapAdPostData;
            postDataService('POST', '/managementconsole/admin-api/ldap/configure', config)
                .then(data => {
                if (data?.Data && data?.Data?.enable_ldap) {
                    const newData = data?.Data || {};
                    if (!newData?.ssl) {
                        newData.ssl = false;
                    }
                    setActiveDirectoryData(newData?.enable_ldap ? { ...newData } : initialData);
                }
                else {
                    const errors = fetchError(data);
                    onError(errors);
                }
            })
                .catch(err => {
                onSaveError(err);
            })
                .finally(() => {
                hideLoader();
            });
        }
    }, [ldapAdPostData]);
    useEffect(() => {
        if (sslCertData || sslCertError) {
            setIsSslRequired(true);
        }
    }, [sslCertData, sslCertError, sslCertLoading]);
    const onFormElementValueChange = (id, value) => {
        if (id === 'ssl') {
            if (value === 'true') {
                const data = formBuilderRef.current
                    .validateAndSubmitForm()
                    .find((record) => record.id === 'url');
                checkSSLCert(data.value);
            }
            else {
                setIsSslRequired(false);
                setSslFieldsError({
                    sslCertificate: '',
                    certificateAlias: '',
                });
            }
        }
    };
    const actionHandler = (type, currentFormData = {}) => {
        switch (type) {
            case ActionButtons.EDIT:
                setMode(Mode.EDIT);
                break;
            case ActionButtons.BACK:
            case ActionButtons.CANCEL:
                sslRef.current = { sslCertificate: null, certificateAlias: '' };
                setMode(Mode.VIEW);
                break;
            case ActionButtons.SAVE:
                onSave(currentFormData);
                break;
            case ActionButtons.DELETE:
                setDeleteConfirm(true);
                break;
            default:
                setMode(Mode.EDIT);
                break;
        }
    };
    const onDeleteConfirm = useCallback(() => {
        showLoader();
        deleteLdapAd();
        setDeleteConfirm(false);
    }, [deleteConfirm]);
    const onDeleteDismiss = useCallback(() => setDeleteConfirm(false), [
        deleteConfirm,
    ]);
    const headerActions = [
        {
            type: ActionButtons.EDIT,
            title: activeDirectoryData?.enable_ldap
                ? t(TranslationsKeys.SAML_MODAL_EDIT)
                : t(TranslationsKeys.SAML_CONFIGURE_LABEL),
        },
        ...(mode === Mode.VIEW && activeDirectoryData?.enable_ldap
            ? [
                {
                    type: ActionButtons.DELETE,
                    title: t(TranslationsKeys.SAML_REMOVE_BUTTON_TEXT),
                },
            ]
            : []),
    ];
    const handleIsEdited = (currentFormData, id) => {
        validateForm(currentFormData, id);
    };
    const getHeaderTitle = useCallback(() => {
        const title = mode === Mode.EDIT
            ? `${t(activeDirectoryData?.enable_ldap
                ? TranslationsKeys.SAML_MODAL_EDIT
                : TranslationsKeys.SAML_CONFIGURE_LABEL)} `
            : '';
        return `${title}${t(TranslationsKeys.AD_HEADING)}`;
    }, [mode]);
    const onUserTestChange = (e) => {
        userTestRef.current[e.target.id] = e.target.value;
        const { username, password } = userTestRef.current;
        if (username && password) {
            setIsUserTestConfirmDisabled(false);
        }
        else {
            setIsUserTestConfirmDisabled(true);
        }
        setIsUserTestFormInvalid(false);
    };
    const onSslFieldChange = (e) => {
        if (e.target.value) {
            sslRef.current[e.target.id] =
                e.target.id === 'sslCertificate'
                    ? e.target.files
                    : e.target.value;
        }
        if (!sslRef.current.sslCertificate ||
            !sslRef.current.certificateAlias) {
            setSslFieldsError({
                ...sslFieldsError,
                [e.target.id]: '',
            });
        }
    };
    return ldapAdError ? (React.createElement(GenericError, null)) : (React.createElement(React.Fragment, null,
        deleteConfirm && (React.createElement(CreateModal, { pageName: pageName, action: "delete", onConfirm: onDeleteConfirm, onDismiss: onDeleteDismiss })),
        React.createElement("div", { className: styles.activeDirectoryContainer, "data-testid": "active_directory-container" },
            !isLdapAdLoading && (React.createElement(Horizontal, { spacing: "d" },
                React.createElement(Vertical, { className: styles.headerContainer },
                    React.createElement(Header, { title: getHeaderTitle(), subTitle: t(TranslationsKeys.AD_CONTENT), titleSize: "large", learnMoreKey: LearnMore.ACTIVE_DIRECTORY, mode: mode, actions: mode === Mode.VIEW
                            ? headerActions
                            : undefined, actionHandler: actionHandler })))),
            !isLdapAdLoading && (React.createElement(React.Fragment, null,
                React.createElement(FormBuilder, { ref: formBuilderRef, itemLayout: "horizontal", viewMode: mode, formFields: formFields, onFormElementValueChange: onFormElementValueChange, isEdited: (edited, id) => handleIsEdited(formBuilderRef.current.onSubmitForm(), id) }),
                mode === Mode.EDIT && isSslRequired && (React.createElement(Vertical, { spacing: "c" },
                    React.createElement(FormControl, { label: t(TranslationsKeysCommon.AD_MAPPING_SSL_CERTIFICATE_LABEL), layout: "horizontal", className: sslFieldsError.sslCertificate
                            ? styles.sslFormControlWithError
                            : styles.sslFormControl, hasError: !!sslFieldsError.sslCertificate, errorMessage: sslFieldsError.sslCertificate },
                        React.createElement(Input.File, { id: "sslCertificate", value: sslRef.current.sslCertificate?.name, onChange: onSslFieldChange, required: true })),
                    React.createElement(FormControl, { label: t(TranslationsKeysCommon.AD_MAPPING_CERTIFICATE_ALIAS), layout: "horizontal", className: sslFieldsError.sslCertificate
                            ? styles.sslFormControlWithError
                            : styles.sslFormControl, hasError: !!sslFieldsError.certificateAlias, errorMessage: sslFieldsError.certificateAlias },
                        React.createElement(Input.Text, { id: "certificateAlias", ariaLabel: "certificateAlias", value: sslRef.current.certificateAlias, onChange: onSslFieldChange, required: true })))),
                mode === Mode.EDIT && (React.createElement(Horizontal, { spacing: "c", hAlignContent: "center" },
                    React.createElement(Button, { onClick: () => actionHandler(ActionButtons.SAVE, formBuilderRef.current.validateAndSubmitForm()), text: t(TranslationsKeys.SAVE), type: "primary", "data-testid": "save" }),
                    React.createElement(Button, { onClick: () => {
                            actionHandler(ActionButtons.CANCEL);
                            setIsSslRequired(false);
                            setSslFieldsError({
                                sslCertificate: '',
                                certificateAlias: '',
                            });
                        }, text: t(TranslationsKeys.CANCEL), type: "secondary", "data-testid": "cancel" })))))),
        isAdTestComponentVisible && (React.createElement(Modal, { title: t(TranslationsKeys.AD_TEST_HEADING), isOpen: isAdTestComponentVisible, onDismiss: () => {
                userTestRef.current = {
                    username: '',
                    password: '',
                };
                setIsUserTestConfirmDisabled(true);
                setIsAdTestComponentVisible(false);
            }, size: 'medium', confirmButton: {
                text: t(TranslationsKeys.AD_TEST_BUTTON),
                isDisabled: isUserTestConfirmDisabled,
                onConfirm: () => {
                    if (isUserTestFormInvalid) {
                        return;
                    }
                    const { username, password } = userTestRef.current;
                    if (!username || !password) {
                        setIsUserTestFormInvalid(true);
                        return;
                    }
                    userTestRef.current = {
                        username: '',
                        password: '',
                    };
                    const { sslCertificate, certificateAlias, } = sslRef.current;
                    const input = {
                        ...formDataRef.current,
                        username,
                        password,
                    };
                    if (input?.ssl) {
                        input.ssl_cert = sslCertificate;
                        input.certificate_alias = certificateAlias;
                    }
                    setIsAdTestComponentVisible(false);
                    setLdapAdPostData(input);
                },
            } },
            React.createElement(Horizontal, { className: styles.userTestModal },
                React.createElement(Vertical, { className: styles.userTestLeftContainer, vAlignContent: "center" },
                    React.createElement("div", { className: styles.authLogo }),
                    React.createElement(Typography, { variant: "caption", className: styles.content }, t(TranslationsKeys.AD_TEST_CONTENT)),
                    React.createElement(FormControl, { label: `${t(TranslationsKeys.AD_TEST_USERNAME_LABEL)} *` },
                        React.createElement(Input.Text, { id: "username", ariaLabel: "username", placeholder: t(TranslationsKeys.AD_TEST_USERNAME_PLACEHOLDER), value: userTestRef.current.username, onChange: onUserTestChange, required: true, suffixIcon: isUserTestFormInvalid &&
                                !userTestRef.current.username
                                ? IconID.EXCLAMATION_POINT_CIRCLE
                                : null, suffixIconColor: IconColor.RED })),
                    React.createElement(FormControl, { label: `${t(TranslationsKeys.AD_TEST_PASSWORD_LABEL)} *` },
                        React.createElement(Input.Text, { className: styles.passwordContainer, id: "password", type: "password", ariaLabel: "password", placeholder: t(TranslationsKeys.AD_TEST_PASSWORD_PLACEHOLDER), value: userTestRef.current.password, onChange: onUserTestChange, required: true, suffixIcon: isUserTestFormInvalid &&
                                !userTestRef.current.password
                                ? IconID.EXCLAMATION_POINT_CIRCLE
                                : null, suffixIconColor: IconColor.RED }))),
                React.createElement(Vertical, { className: styles.userTestRightContainer },
                    React.createElement("div", { className: styles.adUserTestBackground })))))));
};
export const ActiveDirectory = () => (React.createElement(WithQueryHandler, null,
    React.createElement(ActiveDirectoryContainer, null)));
//# sourceMappingURL=active-directory.container.js.map